import { toast } from "react-toastify";
import { AuthService } from "./AuthService";
import { MAX_AUTH_AGE_EDIT, MAX_AUTH_AGE_SENSITIVE } from "../shared/Config";

const PENDING_STATE_KEY = "reauth:pending";
const PENDING_STATE_TTL_MS = 10 * 60 * 1000;
export const REDIRECT_DELAY_MS = 1500;

export type ReauthAction =
  | "update-profile"
  | "update-password"
  | "register-mfa"
  | "remove-mfa"
  | "retry";

export type ReauthPendingState = {
  action: ReauthAction;
  tab: string;
  requestedAt: number;
};

export const REAUTH_MESSAGES = {
  REAUTH_REQUIRED:
    "Por segurança, confirme sua identidade para alterar dados sensíveis. Redirecionando para o login...",
  LOGIN_REQUIRED:
    "Seu login é muito antigo para editar dados. Redirecionando para o login...",
};

async function getAuthTime(): Promise<number | null> {
  try {
    const userManager = await AuthService.getUserManager();
    const user = await userManager.getUser();

    if (!user || !user.access_token) {
      return null;
    }

    const payload = JSON.parse(atob(user.access_token.split(".")[1]));
    const authTime = Number(payload?.auth_time);

    return Number.isFinite(authTime) && authTime > 0 ? authTime : null;
  } catch (error) {
    return null;
  }
}

export const ReauthService = {
  isAuthFresh: async function (maxAgeSeconds: number): Promise<boolean> {
    const authTime = await getAuthTime();

    if (authTime === null) {
      return false;
    }

    const nowInSeconds = Math.floor(Date.now() / 1000);
    return nowInSeconds - authTime <= maxAgeSeconds;
  },

  isAuthFreshForSensitive: function (): Promise<boolean> {
    return ReauthService.isAuthFresh(MAX_AUTH_AGE_SENSITIVE);
  },

  isAuthFreshForEdit: function (): Promise<boolean> {
    return ReauthService.isAuthFresh(MAX_AUTH_AGE_EDIT);
  },

  triggerReauth: async function (
    state: Omit<ReauthPendingState, "requestedAt">,
    message: string = REAUTH_MESSAGES.REAUTH_REQUIRED,
    onRedirectSettled?: () => void
  ): Promise<void> {
    sessionStorage.setItem(
      PENDING_STATE_KEY,
      JSON.stringify({ ...state, requestedAt: Date.now() })
    );

    toast.warning(message);

    const userManager = await AuthService.getUserManager();

    setTimeout(async () => {
      try {
        await userManager.signinRedirect({ prompt: "login" });
      } catch {
        onRedirectSettled?.();
      }
    }, REDIRECT_DELAY_MS);
  },

  peekPendingState: function (): ReauthPendingState | null {
    try {
      const raw = sessionStorage.getItem(PENDING_STATE_KEY);
      if (!raw) {
        return null;
      }

      const state = JSON.parse(raw) as ReauthPendingState & {
        formValues?: unknown;
      };

      if (state.formValues !== undefined) {
        delete state.formValues;
        sessionStorage.setItem(PENDING_STATE_KEY, JSON.stringify(state));
      }

      if (Date.now() - state.requestedAt > PENDING_STATE_TTL_MS) {
        sessionStorage.removeItem(PENDING_STATE_KEY);
        return null;
      }

      return state;
    } catch (error) {
      sessionStorage.removeItem(PENDING_STATE_KEY);
      return null;
    }
  },

  consumePendingState: function (
    action: ReauthAction
  ): ReauthPendingState | null {
    const state = ReauthService.peekPendingState();

    if (!state || state.action !== action) {
      return null;
    }

    sessionStorage.removeItem(PENDING_STATE_KEY);
    return state;
  },
};
